import moment from "moment"
import { useCallback } from "react"
import {
  applyZplLabelOffset,
  DEPOSIT_CODE_AUTO_PRINT_CONFIG_REF_NAME,
  DEPOSIT_CODE_LABEL_OFFSET_X_CONFIG_REF_NAME,
  DEPOSIT_CODE_LABEL_OFFSET_Y_CONFIG_REF_NAME,
  DEPOSIT_CODE_ZPL_TEMPLATE_CONFIG_REF_NAME,
  DepositCodeZplValues,
  renderDepositCodeZpl,
  resolveDepositCodeZplTemplate,
} from "shared/depositCodePrintData"
import { FeatureEnum } from "shared/featureData"
import { useAppSelector } from "src/app/hooks"
import { selectCurrentTenant } from "src/features/me/meSlice"
import useFeatures from "src/features/things/useFeatures"
import useTenantConfig from "src/hooks/useTenantConfig"
import { useBrowserPrintHealth } from "./browserPrintHealth"
import {
  DepositCodeLabelContext,
  renderDepositCodeLabel,
  sendZplToDefaultPrinter,
} from "./depositCodeLabel"

export const SAMPLE_DEPOSIT_CODE_VALUES: DepositCodeZplValues = {
  depositCode: "123456",
  receptionCode: "654321",
  doorLabel: "A1",
  lockerName: "Test",
  reservationNumber: 1000,
  expiration: "2026-12-31 18:00",
}

/** What the Configuration page passes to try unsaved edits. */
export type DepositCodeLabelSettings = {
  template?: string
  offsetX?: number | null
  offsetY?: number | null
}

/**
 * LOCKER-522 — everything a print surface needs: whether the tenant prints at
 * all (the `DepositCodePrinting` feature), its settings, the shared Browser
 * Print health and a `print` that renders the tenant's template.
 */
export const useDepositCodePrinting = (tenantId?: string) => {
  const { canUseFeature } = useFeatures(tenantId)
  const isEnabled = canUseFeature(FeatureEnum.DepositCodePrinting)
  const configs = useTenantConfig(tenantId)
  const tenant = useAppSelector(selectCurrentTenant)
  const health = useBrowserPrintHealth(isEnabled)

  const templateOverride =
    configs[DEPOSIT_CODE_ZPL_TEMPLATE_CONFIG_REF_NAME]?.sVal
  const offsetX = configs[DEPOSIT_CODE_LABEL_OFFSET_X_CONFIG_REF_NAME]?.nVal
  const offsetY = configs[DEPOSIT_CODE_LABEL_OFFSET_Y_CONFIG_REF_NAME]?.nVal
  const autoPrint =
    isEnabled && !!configs[DEPOSIT_CODE_AUTO_PRINT_CONFIG_REF_NAME]?.bVal

  const print = useCallback(
    (context: Omit<DepositCodeLabelContext, "organizationName">) =>
      sendZplToDefaultPrinter(
        applyZplLabelOffset(
          renderDepositCodeLabel(templateOverride, {
            organizationName: tenant?.name,
            ...context,
          }),
          offsetX,
          offsetY,
        ),
      ),
    [templateOverride, offsetX, offsetY, tenant?.name],
  )

  /**
   * A test label with sample values. Takes the settings explicitly so the
   * Configuration page can try an unsaved edit before saving it.
   */
  const printSample = useCallback(
    (settings: DepositCodeLabelSettings = {}) =>
      sendZplToDefaultPrinter(
        applyZplLabelOffset(
          renderDepositCodeZpl(
            resolveDepositCodeZplTemplate(
              "template" in settings ? settings.template : templateOverride,
            ),
            {
              ...SAMPLE_DEPOSIT_CODE_VALUES,
              organizationName: tenant?.name,
              printedAt: moment().format("YYYY-MM-DD HH:mm"),
            },
          ),
          "offsetX" in settings ? settings.offsetX : offsetX,
          "offsetY" in settings ? settings.offsetY : offsetY,
        ),
      ),
    [templateOverride, offsetX, offsetY, tenant?.name],
  )

  return { isEnabled, autoPrint, templateOverride, health, print, printSample }
}
